import { MeshBasicNodeMaterial, Vector2 } from 'three/webgpu'
import {
	texture,
	uv,
	uniform,
	positionLocal,
	vec2,
	vec3,
	float,
	floor,
	fract,
	step,
	smoothstep,
	mix,
	clamp,
	abs,
	max,
	fwidth,
	sin,
	dot,
} from 'three/tsl'

export default class ScreenMaterial extends MeshBasicNodeMaterial {
	static defaults = {
		resolution: [512, 385],
		pitch: [128, 96],
		gap: 0.15,
		strength: 0.5,
		bleed: 0.9,
		brightness: 1.1,
		tint: [1, 1, 1],
		scale: [-1, -1],
		offset: [1, 1],
		interference: 0,
		lines: 72,
		tear: 0.3,
		roll: 0.3,
		bar: 0.55,
		band: 0.1,
		wash: 0.0,
		chroma: 1,
		churn: 24,
		geometry: null,
	}

	constructor(map, options = {}) {
		const {
			resolution,
			pitch,
			gap,
			strength,
			bleed,
			brightness,
			tint,
			scale,
			offset,
			geometry,
			interference,
			lines,
			tear,
			roll,
			bar,
			band,
			wash,
			chroma,
			churn,
		} = { ...ScreenMaterial.defaults, ...options }

		super({ toneMapped: false })

		this.map = map
		this.resolution = uniform(new Vector2(resolution[0], resolution[1]))
		this.pitch = uniform(new Vector2(pitch[0], pitch[1]))
		this.scale = uniform(new Vector2(scale[0], scale[1]))
		this.offset = uniform(new Vector2(offset[0], offset[1]))
		this.gap = uniform(gap)
		this.strength = uniform(strength)
		this.bleed = uniform(bleed)
		this.brightness = uniform(brightness)
		this.interference = uniform(interference)
		this.tear = uniform(tear)
		this.roll = uniform(roll)
		this.tint = uniform(vec3(tint[0], tint[1], tint[2]))
		this.time = uniform(0)

		const projected = ScreenMaterial.project(geometry).mul(this.scale).add(this.offset)
		const beat = floor(this.time.mul(churn))

		const row = floor(projected.y.mul(lines))
		const burst = step(float(1).sub(this.interference), ScreenMaterial.hash(vec2(row.add(7), beat)))
		const jitter = ScreenMaterial.hash(vec2(row, beat)).sub(0.5).mul(this.tear).mul(this.interference).mul(burst)
		const drift = this.time.mul(this.roll).mul(this.interference)

		const tuned = vec2(projected.x.add(jitter), fract(projected.y.add(drift)))
		const footprint = projected.mul(this.pitch)
		const density = max(fwidth(footprint.x).mul(3), fwidth(footprint.y))
		const sharp = smoothstep(float(1), float(0.4), density)

		const cells = tuned.mul(this.resolution)
		const centre = floor(cells).add(0.5).div(this.resolution)

		const leds = tuned.mul(this.pitch)
		const local = fract(leds)

		const sample = texture(this.map, centre).rgb.mul(this.tint)
		const luma = dot(sample, vec3(0.299, 0.587, 0.114))
		const washed = mix(sample, vec3(luma), this.interference.mul(wash))

		const cell = floor(leds).add(beat)
		const grain = ScreenMaterial.hash(cell)
		const speckle = vec3(grain, ScreenMaterial.hash(cell.add(19.7)), ScreenMaterial.hash(cell.add(43.3)))
		const patch = ScreenMaterial.hash(floor(leds.mul(0.12)).add(beat))
		const snow = clamp(this.interference.mul(mix(float(0.35), float(1.3), patch)), 0, 1)

		const hold = fract(this.time.mul(bar).negate())
		const bandEdge = abs(fract(tuned.y.sub(hold).add(0.5)).sub(0.5)).mul(2)
		const rolling = smoothstep(float(band), 0, bandEdge).mul(this.interference)

		const noise = mix(vec3(grain), speckle, float(chroma))
		const signal = mix(washed, noise, snow).mul(mix(float(1), float(0.15), rolling))
		const stripe = floor(local.x.mul(3))
		const mask = vec3(step(stripe, float(0.5)), step(abs(stripe.sub(1)), float(0.5)), step(float(1.5), stripe))
		const edge = max(abs(fract(local.x.mul(3)).sub(0.5)), abs(local.y.sub(0.5))).mul(2)
		const grid = smoothstep(float(1).sub(this.gap), float(1), edge).oneMinus()
		const subpixels = mix(vec3(1), mask.mul(3), this.bleed.mul(sharp))
		const panel = signal.mul(subpixels).mul(mix(float(1), grid, sharp))

		const bite = mix(this.strength, float(1), this.interference)

		this.colorNode = mix(signal, panel, bite).mul(this.brightness)
	}

	static hash(cell) {
		return fract(sin(dot(cell, vec2(12.9898, 78.233))).mul(43758.5453))
	}

	static project(geometry) {
		if (!geometry || geometry.attributes.uv) return uv()

		geometry.computeBoundingBox()

		const { min, max: bound } = geometry.boundingBox
		const size = bound.clone().sub(min)
		const [, height, width] = ['x', 'y', 'z'].sort((a, b) => size[a] - size[b])
		const normalised = positionLocal.sub(vec3(min.x, min.y, min.z)).div(vec3(size.x, size.y, size.z))

		return vec2(normalised[width], normalised[height])
	}
}
